import { forwardRef, useEffect, useState } from 'react';
import { useHierarchicalMenu, useInstantSearch } from 'react-instantsearch';
import { Popover } from '@headlessui/react';
import { oneLine } from 'common-tags';

import ChevronDown from '@/icons/light/chevron-down.svg';
import ChevronUp from '@/icons/light/chevron-up.svg';

import { gtmPush } from '@/utils';
import { getLastRefinedItem, titleCaseZone } from '../utils';

import type { FunctionComponent, Ref } from 'react';
import type { TypeHierarchicalMenuProps } from './types';

function isModifierClick(event: React.MouseEvent) {
  const isMiddleClick = event.button === 1;

  return Boolean(
    isMiddleClick ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey,
  );
}

function HierarchicalList({
  className = '',
  classNames = {
    list: '',
    item: '',
  },
  items,
  // nbHits,
  hrefForItem,
  onNavigate,
}: any) {
  return (
    <ul
      className={`${classNames.list} [&_ul_ul_a]:pl-12 [&_ul_ul_ul_a]:pl-16 [&_ul_ul_ul_ul_a]:pl-20`}
    >
      {items.map((item: any) => {
        const Icon = item.isRefined ? ChevronUp : ChevronDown;

        return (
          <li key={item.value}>
            <a
              href={hrefForItem(item)}
              // `text-body text-body-s` en el propio <a>: la regla base
              // global `a { text-link }` (13px) gana a la tipografía heredada
              // del panel, así que hay que declararla en el elemento.
              className={`text-body text-body-s hover:bg-neutrals-50 flex cursor-pointer items-center justify-between gap-4 py-3 pr-2 pl-3 ${
                item.isRefined
                  ? 'text-text-neutrals-900 font-bold'
                  : 'text-text-neutrals-500'
              } ${className}`}
              onClick={(event) => {
                if (isModifierClick(event)) {
                  return;
                }
                event.preventDefault();
                onNavigate(item);
              }}
            >
              <div className="flex flex-row items-center gap-1">
                <input
                  type="checkbox"
                  checked={item.isRefined}
                  readOnly
                  className="border-neutrals-500 checked:text-primary-500 h-4 w-4 cursor-pointer rounded-[2px] checked:ring-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-hidden"
                />
                <span>{titleCaseZone(item.label)}</span>
              </div>

              <div className="flex flex-row items-center gap-1">
                <span className="text-text-neutrals-300">{item.count}</span>
                {item.canRefine && (
                  <Icon className="h-6 w-6" aria-hidden="true" />
                )}
              </div>
            </a>

            {item.data && (
              <HierarchicalList
                items={item.data!}
                onNavigate={onNavigate}
                hrefForItem={hrefForItem}
                className="border-neutrals-50 border-b pl-8 last:border-0"
                classNames={{ list: 'divide-y divide-neutrals-50' }}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export const HierarchicalMenu: FunctionComponent<TypeHierarchicalMenuProps> =
  forwardRef(
    (
      {
        className = '',
        classNames = {
          button: '',
          panel: '',
        },
        navigate,
        hrefFor,
        ...props
      }: TypeHierarchicalMenuProps,
      ref: Ref<HTMLDivElement> | undefined,
    ) => {
      // const { results } = useHits();
      const { items, refine, createURL } = useHierarchicalMenu(props);
      const { results } = useInstantSearch();

      // Destino al pulsar un item: si ya está refinado, "sube" a su padre
      // (o al índice sin zona si es de primer nivel); si no, "baja" a él.
      const parentOf = (value: string): string | null => {
        const segs = value.split(' > ');
        return segs.length > 1 ? segs.slice(0, -1).join(' > ') : null;
      };
      const targetFor = (item: any): string | null =>
        item.isRefined ? parentOf(item.value) : item.value;
      const hrefForItem = (item: any): string =>
        hrefFor ? hrefFor(targetFor(item)) : createURL(item.value);
      const DEFAULT_VALUE = 'Todos los pisos';
      const refinedValue = getLastRefinedItem(items);

      // Con una zona refinada, el conector sólo trae UN nivel más: para
      // llegar a una calle había que navegar nivel a nivel. Esta query extra
      // de sólo-facets pide TODOS los niveles que faltan por debajo de la
      // selección (con Madrid refinado: ciudades ya vienen; se piden zonas y
      // calles) y se injertan cada uno bajo su padre, de modo que el subárbol
      // completo de la selección es clicable de una vez. Se reutiliza el
      // param string de la búsqueda actual para que los counts respeten los
      // filtros activos (precio, habitaciones, bounds del mapa...). Sin
      // selección no se pide nada: el desplegable raíz no se infla con todas
      // las provincias expandidas.
      const refinedDepth = refinedValue?.value.split(' > ').length ?? 0;
      const missingLevels: string[] = [];
      for (
        let level = refinedDepth + 1;
        refinedDepth > 0 && level <= 3;
        level++
      ) {
        missingLevels.push(`hierarchicalZonas.lvl${level}`);
      }
      const [subLevelsByParent, setSubLevelsByParent] = useState<
        Record<string, any[]>
      >({});
      const searchParams = (results as any)?.params as string | undefined;

      useEffect(() => {
        if (!missingLevels.length || !searchParams) {
          setSubLevelsByParent({});
          return undefined;
        }
        const sp = new URLSearchParams(searchParams);
        sp.set('facets', JSON.stringify(missingLevels));
        sp.set('hitsPerPage', '0');
        sp.delete('page');

        const appId = process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_APP_ID ?? '';
        let cancelled = false;
        fetch(
          `https://${appId}-dsn.algolia.net/1/indexes/${(results as any).index}/query`,
          {
            method: 'POST',
            headers: {
              'X-Algolia-Application-Id': appId,
              'X-Algolia-API-Key':
                process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY ?? '',
            },
            body: JSON.stringify({ params: sp.toString() }),
          },
        )
          .then((response) => response.json())
          .then((data) => {
            if (cancelled) return;
            const grouped: Record<string, any[]> = {};
            missingLevels.forEach((attr) => {
              Object.entries(data?.facets?.[attr] ?? {}).forEach(
                ([value, count]) => {
                  const segments = value.split(' > ');
                  const parent = segments.slice(0, -1).join(' > ');
                  (grouped[parent] = grouped[parent] ?? []).push({
                    value,
                    label: segments[segments.length - 1],
                    count,
                    isRefined: false,
                    canRefine: false,
                    data: null,
                  });
                },
              );
            });
            Object.values(grouped).forEach((children) =>
              children.sort((a, b) => a.label.localeCompare(b.label)),
            );
            setSubLevelsByParent(grouped);
          })
          .catch(() => {});
        return () => {
          cancelled = true;
        };
      }, [missingLevels.join(), refinedValue?.value, searchParams]);

      // Injerta recursivamente los niveles pedidos bajo su padre. Los hijos
      // que el conector ya trae (con isRefined bien puesto) tienen prioridad;
      // los injertados buscan a su vez sus propios hijos en el mapa.
      const graftSubLevels = (nodes: any[]): any[] =>
        nodes.map((node) => {
          const children = node.data ?? subLevelsByParent[node.value] ?? null;
          return { ...node, data: children ? graftSubLevels(children) : null };
        });
      const displayItems = graftSubLevels(items);

      const [zones, setZones] = useState();
      useEffect(() => {
        if (
          refinedValue?.value.split(' > ')?.toString() !==
          (zones || '')?.toString()
        ) {
          setZones(refinedValue?.value.split(' > '));
        }
      }, [refinedValue]);

      useEffect(() => {
        const province = zones?.[0];
        const city = zones?.[1];
        const cityArea = zones?.[2];

        gtmPush({
          event: 'parameter_filter',
          filter_action: refinedValue?.isRefined
            ? 'apply_filter'
            : 'clear_all_filter',
          filter_price_min: undefined,
          filter_price_max: undefined,
          filter_size_min: undefined,
          filter_size_max: undefined,
          filter_property_type: undefined,
          filter_property_condition: undefined,
          filter_province: province,
          filter_city: city,
          filter_city_area: cityArea,
          filter_results_count: refinedValue?.count,
        });
      }, [zones]);

      return (
        // `className` va en el Popover, que es el elemento que participa como
        // hijo flex del contenedor. Puesto sólo en el div interior, utilidades
        // como `flex-1` no tenían ningún efecto.
        <Popover className={className}>
          {({ open, close }) => {
            const Icon = open ? ChevronUp : ChevronDown;
            return (
              <div className="text-body text-body-s relative">
                <Popover.Button
                  className={oneLine`text-placeholder bg-neutrals-0 relative w-full rounded-full border py-3 pr-10 pl-4 ${open ? 'border-neutrals-900' : 'border-neutrals-500'} ${classNames.button} `}
                >
                  <span className="block truncate text-left font-normal">
                    {refinedValue?.value
                      ? refinedValue.value
                          .split(' > ')
                          .map(titleCaseZone)
                          .join(' > ')
                      : DEFAULT_VALUE}
                  </span>

                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </Popover.Button>

                <Popover.Panel
                  ref={ref}
                  unmount={false}
                  className={`rounded-custom border-neutrals-100 bg-bg-neutrals-0 shadow-light-s absolute z-10 mt-1 max-h-64 w-full min-w-[250px] overflow-auto overflow-y-scroll border p-1 ${classNames.panel}`}
                >
                  <HierarchicalList
                    items={displayItems}
                    // nbHits={refinedValue?.count || results?.nbHits}
                    onNavigate={(item: any) => {
                      if (navigate) {
                        navigate(targetFor(item));
                      } else {
                        refine(item.value);
                      }
                      close();
                    }}
                    hrefForItem={hrefForItem}
                    className="border-neutrals-50 border-b last:border-0"
                    classNames={{ list: 'divide-y divide-neutrals-50' }}
                  />
                </Popover.Panel>
              </div>
            );
          }}
        </Popover>
      );
    },
  );

HierarchicalMenu.displayName = 'HierarchicalMenu';
